/*** Neural Network AI — drop-in replacement for HeuristicAi's strategic planning ***/

import { getTf } from './tf_provider.js';
import { Strategy, TacticalAi } from './ai.js';
import {
    encodeGameState,
    encodeSpawnerFeatures,
    STATE_ENCODER_VERSION_V2,
    getRoleSlotMapping
} from './state_encoder.js';
import { MAX_SPAWNERS, MAX_SPAWNER_FEATURES_V2, maskedSoftmax, unpackModelPrediction } from './model.js';
import { postInferRequest } from './nn_worker_client.js';

/**
 * @typedef {{ policy: Float32Array, value: number,
 *             easyPickings: Float32Array|null, contestRisk: Float32Array|null,
 *             failed?: boolean }} InferResult
 */

/**
 * NeuralNetAi extends TacticalAi, replacing rule-based strategic planning with CNN inference.
 *
 * Two execution modes:
 *  - **Sync** (headless/training): pass a `syncModel` TF.js LayersModel. `runStrategicPlan()`
 *    runs inference synchronously on the calling thread, same as the original behaviour.
 *  - **Worker** (browser): omit `syncModel`. Inference is dispatched to the shared Web Worker
 *    and results are applied as soon as they arrive (the worker callback updates
 *    spawnerAnalysis priorities mid plan-cycle, so the in-flight `_drainSendQueue`
 *    picks up the freshest priorities for any units still left to process).
 */
class NeuralNetAi extends TacticalAi {

    /**
     * @param {number} faction
     * @param {import('./util.js').EntitySystem} entitySystem
     * @param {string} modelVersion - 'v1' or 'v2'
     * @param {import('./util.js').GridSystem} locationGrid - For state encoding
     * @param {number} worldWidth
     * @param {number} worldHeight
     * @param {any} [syncModel] - Optional pre-loaded TF.js LayersModel for synchronous inference.
     *   Pass this in headless/Node.js environments (training, evaluation). Omit in the browser
     *   to use the shared Web Worker instead.
     */
    constructor(faction, entitySystem, modelVersion, locationGrid, worldWidth, worldHeight, syncModel = null) {
        super(faction, entitySystem, locationGrid);
        this.aiTypeName = 'neural';
        /** @type {string} */
        this.modelVersion = modelVersion || STATE_ENCODER_VERSION_V2;
        /** @type {number} */
        this.worldWidth = worldWidth;
        /** @type {number} */
        this.worldHeight = worldHeight;
        /**
         * When non-null, inference runs synchronously on this thread (headless/training mode).
         * When null, inference is dispatched to the shared Web Worker (browser mode).
         * @type {any}
         */
        this.syncModel = syncModel;

        /** @type {InferResult|null} Most recent worker inference result (applied on arrival). */
        this._pendingResult = null;

        /** @type {Float32Array|null} */
        this.lastPolicyOutput = null;
        /** @type {number|null} */
        this.lastValueOutput = null;
        /** @type {Float32Array|null} */
        this.lastEasyPickingsOutput = null;
        /** @type {Float32Array|null} */
        this.lastContestRiskOutput = null;
        /** @type {Float32Array|null} */
        this.lastSpawnerFeatures = null;

        /**
         * When true, record (state, action) pairs for imitation learning.
         * @type {boolean}
         */
        this.recording = false;
        /** @type {Array<{state: Float32Array, stateShape: number[], spawnerFeatures: Float32Array, numSpawners: number, action: Float32Array}>} */
        this.recordedTransitions = [];
    }

    /**
     * Override: replace rule-based planning with CNN inference.
     * In sync mode runs the forward pass immediately. In worker mode dispatches
     * the next encoding; worker results are applied directly from the onmessage
     * callback as soon as they arrive, so spawnerAnalysis priorities can update
     * mid-cycle and the in-flight send-queue drain picks them up automatically.
     */
    runStrategicPlan() {
        if (this.syncModel) {
            this._runSyncInference();
        } else {
            this._encodeAndDispatch();
        }
    }

    // ─── Sync path (headless / training) ──────────────────────────────────────

    _runSyncInference() {
        const tf = getTf();
        const numSpawners = this.spawnerAnalysis.length;

        tf.tidy(() => {
            const snapshot = this.buildPlanningSnapshot();
            const encoded = snapshot.encoded;

            const gridInput = tf.tensor4d(
                encoded.data,
                [1, encoded.shape[0], encoded.shape[1], encoded.shape[2]]
            );
            let prediction;
            if (snapshot.modelVersion === STATE_ENCODER_VERSION_V2 && snapshot.paddedSpawnerFeatures) {
                const spawnerInput = tf.tensor3d(
                    snapshot.paddedSpawnerFeatures,
                    [1, MAX_SPAWNERS, MAX_SPAWNER_FEATURES_V2]
                );
                prediction = this.syncModel.predict([gridInput, spawnerInput]);
            } else {
                prediction = this.syncModel.predict(gridInput);
            }

            const { policyLogits, value, easyPickings, contestRisk } = unpackModelPrediction(prediction);
            if (!policyLogits || !value) {
                this.lastPolicyOutput = null;
                this.lastValueOutput = 0;
                this.lastEasyPickingsOutput = null;
                this.lastContestRiskOutput = null;
            } else {
                const policy = maskedSoftmax(policyLogits, numSpawners);
                this.lastPolicyOutput = new Float32Array(policy.dataSync());
                this.lastValueOutput = value.dataSync()[0];
                this.lastEasyPickingsOutput = easyPickings
                    ? new Float32Array(easyPickings.dataSync()) : null;
                this.lastContestRiskOutput = contestRisk
                    ? new Float32Array(contestRisk.dataSync()) : null;
                this.lastSpawnerFeatures = new Float32Array(snapshot.spawnerFeatures.data);
            }
        });

        this._applyLastOutputToSpawners();
    }

    // ─── Worker path (browser) ─────────────────────────────────────────────────

    /**
     * Apply the latest worker inference result to spawnerAnalysis priorities.
     * Called from the worker onmessage callback as soon as a result arrives.
     * Falls back to uniform priorities if no result has arrived yet (model still loading).
     */
    _applyPendingResult() {
        const result = this._pendingResult;
        if (result && !result.failed) {
            this.lastPolicyOutput = result.policy;
            this.lastValueOutput = result.value;
            this.lastEasyPickingsOutput = result.easyPickings || null;
            this.lastContestRiskOutput = result.contestRisk || null;
        }
        this._applyLastOutputToSpawners();
    }

    /**
     * Encode current state and post to worker. When the worker responds, apply the
     * result immediately so spawnerAnalysis priorities and unitsToSend reflect the
     * new policy for any units still left to drain in the current send cycle.
     */
    _encodeAndDispatch() {
        const snapshot = this.buildPlanningSnapshot();
        const numSpawners = this.spawnerAnalysis.length;
        postInferRequest(this.faction, snapshot, numSpawners, this.modelVersion, (result) => {
            this._pendingResult = result;
            this._applyPendingResult();
        });
    }

    // ─── Shared helpers ────────────────────────────────────────────────────────

    /** Apply this.lastPolicyOutput (and aux heads) to spawnerAnalysis + set strategy. */
    _applyLastOutputToSpawners() {
        const value = this.lastValueOutput ?? 0;
        if (value > 0.2) {
            this.strategy = Strategy.Attack;
        } else if (value < -0.2) {
            this.strategy = Strategy.Defend;
        } else {
            this.strategy = Strategy.Maintain;
        }

        const numSpawners = this.spawnerAnalysis.length;
        const globalMyUnitCount = this.entitySystem.unitCount[this.faction];
        this.prioritySum = 0;

        if (!this.lastPolicyOutput || numSpawners === 0) {
            for (let s = 0; s < numSpawners; s += 1) {
                this.spawnerAnalysis[s].priority = 1;
                this.prioritySum += 1;
            }
        } else {
            for (let s = 0; s < numSpawners; s += 1) {
                const weight = s < this.lastPolicyOutput.length ? this.lastPolicyOutput[s] : 0;
                const easy = this.lastEasyPickingsOutput && s < this.lastEasyPickingsOutput.length
                    ? this.lastEasyPickingsOutput[s] : 0;
                const risk = this.lastContestRiskOutput && s < this.lastContestRiskOutput.length
                    ? this.lastContestRiskOutput[s] : 0;
                const adj = weight * Math.max(0.25, 1 + easy * 0.25 - risk * 0.15);
                this.spawnerAnalysis[s].priority = Math.max(0.001, adj);
                this.prioritySum += this.spawnerAnalysis[s].priority;
            }
        }

        for (let s = 0; s < numSpawners; s += 1) {
            this.spawnerAnalysis[s].unitsToSend =
                globalMyUnitCount * this.spawnerAnalysis[s].priority / this.prioritySum;
        }
    }

    padSpawnerFeatures(spawnerFeatures) {
        const padded = new Float32Array(MAX_SPAWNERS * MAX_SPAWNER_FEATURES_V2);
        const count = Math.min(MAX_SPAWNERS, spawnerFeatures.numSpawners);
        const width = Math.min(MAX_SPAWNER_FEATURES_V2, spawnerFeatures.featuresPerSpawner);
        for (let i = 0; i < count; i += 1) {
            for (let f = 0; f < width; f += 1) {
                padded[i * MAX_SPAWNER_FEATURES_V2 + f] =
                    spawnerFeatures.data[i * spawnerFeatures.featuresPerSpawner + f];
            }
        }
        return padded;
    }

    buildPlanningSnapshot() {
        const roleSlotMapping = getRoleSlotMapping(this.locationGrid, this.faction);
        const encoded = encodeGameState(
            this.locationGrid,
            this.entitySystem,
            this.faction,
            { version: STATE_ENCODER_VERSION_V2, roleSlotMapping }
        );
        const spawnerFeats = encodeSpawnerFeatures(
            this.spawnerAnalysis,
            this.faction,
            this.worldWidth,
            this.worldHeight,
            { version: STATE_ENCODER_VERSION_V2, roleSlotMapping }
        );
        return {
            modelVersion: STATE_ENCODER_VERSION_V2,
            encoded,
            spawnerFeatures: spawnerFeats,
            paddedSpawnerFeatures: this.padSpawnerFeatures(spawnerFeats)
        };
    }

    /**
     * Record the current state-action pair for imitation learning.
     * @param {Float32Array} heuristicPriorities - Normalized priority weights from the heuristic AI
     */
    recordTransition(heuristicPriorities) {
        if (!this.recording) return;

        const snapshot = this.buildPlanningSnapshot();
        const encoded = snapshot.encoded;
        const spawnerFeats = snapshot.spawnerFeatures;

        this.recordedTransitions.push({
            state: new Float32Array(encoded.data),
            stateShape: encoded.shape.slice(),
            spawnerFeatures: new Float32Array(spawnerFeats.data),
            numSpawners: spawnerFeats.numSpawners,
            action: new Float32Array(heuristicPriorities),
            encoderVersion: snapshot.modelVersion
        });
    }

    /**
     * @returns {Array}
     */
    flushRecordedTransitions() {
        const transitions = this.recordedTransitions;
        this.recordedTransitions = [];
        return transitions;
    }
}

export { NeuralNetAi };
